<script lang="ts" setup>
    import { computed, ref, onMounted } from 'vue'
    import { useLightbox } from '../composables/useLightbox'

    const props = defineProps<{
        mediaType: string
        url: string
        filename?: string
        /** Size in bytes, when known (e.g. local uploads). */
        size?: number
        loading?: boolean
        error?: string
        removable?: boolean
        /**
         * Dense layout for the composer: a small square thumbnail / slim row,
         * without the metadata caption. The full card is reserved for the
         * message log where the details matter.
         */
        compact?: boolean
    }>()

    defineEmits<{
        remove: []
    }>()

    const isImage = computed(() => props.mediaType.startsWith('image/'))

    // --- File-type identity (icon + accent + human kind label) ---

    const extension = computed(() => {
        const name = props.filename ?? ''
        const dot = name.lastIndexOf('.')
        return dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
    })

    // mediaType subtype, e.g. "pdf" from "application/pdf"
    const subtype = computed(
        () => props.mediaType.split('/')[1]?.split(';')[0] ?? '',
    )

    type FileKind = {
        icon: string
        // CSS color driving the icon glyph + tile tint
        accent: string
        label: string
    }

    const KINDS: Array<{ test: RegExp; kind: FileKind }> = [
        {
            test: /pdf/,
            kind: {
                icon: 'ri:file-pdf-2-line',
                accent: '#e2483d',
                label: 'PDF',
            },
        },
        {
            // Match Word specifically: the generic `document` substring also
            // appears in the XLSX/PPTX OOXML mime types (…officedocument…), so
            // matching it here would mis-label spreadsheets/decks as Word.
            test: /msword|wordprocessingml/,
            kind: {
                icon: 'ri:file-word-2-line',
                accent: '#2b7de9',
                label: 'Word',
            },
        },
        {
            test: /sheet|excel|csv/,
            kind: {
                icon: 'ri:file-excel-2-line',
                accent: '#1f9d57',
                label: 'Spreadsheet',
            },
        },
        {
            test: /presentation|powerpoint/,
            kind: {
                icon: 'ri:file-ppt-2-line',
                accent: '#d98324',
                label: 'Presentation',
            },
        },
        {
            test: /zip|compressed|x-7z|x-rar|x-tar|gzip/,
            kind: {
                icon: 'ri:file-zip-line',
                accent: '#c08a1e',
                label: 'Archive',
            },
        },
        {
            test: /^audio\//,
            kind: {
                icon: 'ri:file-music-line',
                accent: '#8b5cf6',
                label: 'Audio',
            },
        },
        {
            test: /^video\//,
            kind: {
                icon: 'ri:film-line',
                accent: '#6366f1',
                label: 'Video',
            },
        },
        {
            test: /json|javascript|typescript|xml|html|x-sh|x-python/,
            kind: {
                icon: 'ri:file-code-line',
                accent: '#0d9488',
                label: 'Code',
            },
        },
        {
            test: /^text\//,
            kind: {
                icon: 'ri:file-text-line',
                accent: '#64748b',
                label: 'Text',
            },
        },
    ]

    const fileKind = computed<FileKind>(() => {
        const haystack = `${props.mediaType} ${extension.value}`
        for (const { test, kind } of KINDS) {
            if (test.test(haystack)) {
                return kind
            }
        }
        return {
            icon: 'ri:file-line',
            accent: '#64748b',
            label: 'File',
        }
    })

    // Short type tag shown in the meta line: prefer the real extension, fall
    // back to the mime subtype, then the generic kind label.
    const typeTag = computed(() =>
        (
            extension.value ||
            subtype.value ||
            fileKind.value.label
        ).toUpperCase(),
    )

    // --- Size & dimensions ---

    const formattedSize = computed(() => {
        const bytes = props.size
        if (bytes === undefined || bytes < 0) {
            return undefined
        }
        if (bytes < 1024) {
            return `${bytes} B`
        }
        const units = ['KB', 'MB', 'GB', 'TB']
        let value = bytes / 1024
        let unitIndex = 0
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024
            unitIndex += 1
        }
        return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unitIndex]}`
    })

    // Thumbnail load lifecycle. Driving the spinner and the error fallback
    // off the real <img> events (not a second background fetch) means the
    // image is requested exactly once and reuses the browser cache on any
    // later re-render, instead of re-downloading every time the message list
    // repaints.
    // Not `useImageFallback`: this tile also needs `loading` vs `loaded` and the
    // decoded dimensions, cached across remounts, so the error flag is one field
    // of a richer per-url entry rather than a standalone broken-url set.
    type ThumbStatus = 'loading' | 'loaded' | 'error'

    // Module-level, keyed by URL: survives component recreation. In polled
    // views (e.g. admin chat-logs, refreshed every few seconds) the whole
    // message array is replaced on each poll, so this component can be torn
    // down and rebuilt. Seeding from the cache means a URL that already
    // decoded starts in `loaded` (no spinner flash, no re-decode) instead of
    // flickering back through `loading`. This is a client-side, per-session
    // cache (no server/Redis involved); bounded to a max size so a long-lived
    // session browsing many images can't grow it without limit — the oldest
    // entry is evicted first (Map preserves insertion order).
    type ThumbEntry = {
        status: ThumbStatus
        dimensions?: { width: number; height: number }
    }
    const THUMB_CACHE_MAX = 300
    const thumbCache = new Map<string, ThumbEntry>()
    const setThumbCache = (url: string, entry: ThumbEntry) => {
        // Re-insert to mark as most-recently used, then trim from the front.
        thumbCache.delete(url)
        thumbCache.set(url, entry)
        if (thumbCache.size > THUMB_CACHE_MAX) {
            thumbCache.delete(thumbCache.keys().next().value as string)
        }
    }

    const cached = thumbCache.get(props.url)
    const thumbStatus = ref<ThumbStatus>(cached?.status ?? 'loading')

    // Natural pixel dimensions, captured once the thumbnail decodes. Doubles
    // as the size hint PhotoSwipe needs for its slide (falls back until known).
    const dimensions = ref<{ width: number; height: number } | undefined>(
        cached?.dimensions,
    )
    const onImageLoad = (event: Event) => {
        const img = event.target as HTMLImageElement
        if (img.naturalWidth && img.naturalHeight) {
            dimensions.value = {
                width: img.naturalWidth,
                height: img.naturalHeight,
            }
        }
        thumbStatus.value = 'loaded'
        setThumbCache(props.url, {
            status: 'loaded',
            dimensions: dimensions.value,
        })
    }
    const onImageError = () => {
        thumbStatus.value = 'error'
        setThumbCache(props.url, { status: 'error' })
    }

    const pswpSize = computed(() => dimensions.value ?? DEFAULT_PSWP_SIZE)

    // Meta fragments joined with " · " for the caption / doc subtitle.
    const imageMeta = computed(() =>
        [
            dimensions.value &&
                `${dimensions.value.width}×${dimensions.value.height}`,
            formattedSize.value,
        ].filter(Boolean),
    )
    const docMeta = computed(() =>
        [typeTag.value, formattedSize.value].filter(Boolean),
    )

    // --- Lightbox ---

    // Slide size PhotoSwipe assumes before the real dimensions are known.
    const DEFAULT_PSWP_SIZE = { width: 1200, height: 800 }

    const galleryRef = ref<HTMLElement>()
    const { init } = useLightbox({ gallery: galleryRef, loop: false })

    onMounted(async () => {
        if (!isImage.value) {
            return
        }
        await init()
    })
</script>

<template>
    <!-- Image attachment: a thumbnail tile with a metadata caption -->
    <figure
        v-if="isImage"
        class="pk-chatbot-file-preview pk-chatbot-file-preview--image"
        :class="{ 'pk-chatbot-file-preview--compact': compact }">
        <div ref="galleryRef" class="pk-chatbot-file-preview__thumb-wrap">
            <a
                :href="url"
                :data-pswp-src="url"
                :data-pswp-width="pswpSize.width"
                :data-pswp-height="pswpSize.height"
                :data-pswp-caption="filename ?? ''"
                class="pk-chatbot-file-preview__thumb-link"
                :aria-label="filename ?? $t('label.file')">
                <img
                    v-show="thumbStatus === 'loaded'"
                    :src="url"
                    :alt="filename"
                    decoding="async"
                    class="pk-chatbot-file-preview__thumb"
                    @load="onImageLoad"
                    @error="onImageError" />
            </a>
            <!-- Spinner until the thumbnail decodes (or while an upload is in
                 flight); a fixed box keeps this from shifting the layout. -->
            <span
                v-if="loading || thumbStatus === 'loading'"
                class="pk-chatbot-file-preview__status pk-chatbot-file-preview__status--overlay">
                <VvIcon name="line-md:loading-loop" />
            </span>
            <!-- Failed to load: a quiet icon instead of the browser's broken
                 image glyph. -->
            <span
                v-else-if="thumbStatus === 'error'"
                class="pk-chatbot-file-preview__status pk-chatbot-file-preview__status--overlay pk-chatbot-file-preview__status--error">
                <VvIcon name="ri:image-2-line" />
            </span>
        </div>
        <figcaption v-if="!compact" class="pk-chatbot-file-preview__caption">
            <span
                v-if="filename"
                class="pk-chatbot-file-preview__name"
                :title="filename"
                >{{ filename }}</span
            >
            <span class="pk-chatbot-file-preview__meta">
                <span
                    v-if="error"
                    class="pk-chatbot-file-preview__error"
                    :title="error">
                    <VvIcon name="ri:error-warning-line" />
                    {{ error }}
                </span>
                <template v-else>{{ imageMeta.join(' · ') }}</template>
            </span>
        </figcaption>
        <button
            v-if="removable && !loading"
            type="button"
            class="pk-chatbot-file-preview__remove"
            :title="$t('action.remove')"
            :aria-label="$t('action.remove')"
            @click.prevent="$emit('remove')">
            <VvIcon name="ri:close-line" />
        </button>
    </figure>

    <!-- Document attachment: an icon tile + name + type · size -->
    <div
        v-else
        class="pk-chatbot-file-preview pk-chatbot-file-preview--doc"
        :class="{
            'pk-chatbot-file-preview--error': error,
            'pk-chatbot-file-preview--compact': compact,
        }">
        <span
            class="pk-chatbot-file-preview__icon-tile"
            :style="{ '--file-accent': fileKind.accent }">
            <VvIcon
                v-if="loading"
                name="line-md:loading-loop"
                class="pk-chatbot-file-preview__icon" />
            <VvIcon
                v-else
                :name="fileKind.icon"
                class="pk-chatbot-file-preview__icon" />
        </span>
        <span class="pk-chatbot-file-preview__body">
            <span
                v-if="filename"
                class="pk-chatbot-file-preview__name"
                :title="filename"
                >{{ filename }}</span
            >
            <span class="pk-chatbot-file-preview__meta">
                <span
                    v-if="error"
                    class="pk-chatbot-file-preview__error"
                    :title="error">
                    <VvIcon name="ri:error-warning-line" />
                    {{ error }}
                </span>
                <template v-else>{{ docMeta.join(' · ') }}</template>
            </span>
        </span>
        <a
            v-if="!loading && !error && !compact"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="pk-chatbot-file-preview__open"
            :title="$t('action.download')"
            :aria-label="`${$t('action.download')}${filename ? ` — ${filename}` : ''}`">
            <VvIcon name="ri:download-2-line" />
        </a>
        <button
            v-if="removable && !loading"
            type="button"
            class="pk-chatbot-file-preview__remove"
            :class="{ 'pk-chatbot-file-preview__remove--inline': !compact }"
            :title="$t('action.remove')"
            :aria-label="$t('action.remove')"
            @click="$emit('remove')">
            <VvIcon name="ri:close-line" />
        </button>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-file-preview {
        // Concentric radii: outer card and inner tiles share an 8px step.
        --file-radius: var(--rounded-lg, 12px);
        position: relative;
        max-width: 280px;
        background-color: var(--color-surface-1);
        border: 1px solid var(--color-surface-3);
        border-radius: var(--file-radius);
        color: var(--color-word-2);
        font-size: var(--text-12);

        &--error {
            border-color: var(--color-danger);
        }

        // --- Document card ---
        &--doc {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            padding: var(--spacing-8);
            min-width: 0;
            transition:
                border-color 0.18s var(--ease-out),
                background-color 0.18s var(--ease-out),
                box-shadow 0.18s var(--ease-out);

            &:hover {
                border-color: var(--color-surface-4);
                background-color: var(--color-surface-2);
                box-shadow: var(--shadow-sm);
            }
        }

        &__icon-tile {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: var(--spacing-36);
            height: var(--spacing-36);
            // One step down from the card radius keeps the nested tile concentric.
            border-radius: var(--rounded-md, 6px);
            // Tint derived from the file-type accent; glyph sits at full accent.
            background-color: color-mix(
                in srgb,
                var(--file-accent) 14%,
                transparent
            );
            color: var(--file-accent);
        }

        &__icon {
            width: var(--spacing-20);
            height: var(--spacing-20);
        }

        &__body {
            display: flex;
            flex-direction: column;
            gap: 1px;
            min-width: 0;
            flex: 1;
        }

        &__open {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: var(--spacing-28);
            height: var(--spacing-28);
            margin-inline-start: var(--spacing-4);
            border-radius: var(--rounded);
            color: var(--color-word-3);
            text-decoration: none;
            opacity: 0;
            transition:
                opacity 0.18s var(--ease-out),
                color 0.18s var(--ease-out),
                background-color 0.18s var(--ease-out);

            &:hover {
                background-color: var(--color-surface-3);
                color: var(--color-word-1);
            }
        }

        &--doc:hover &__open,
        &--doc:focus-within &__open {
            opacity: 1;
        }

        // Touch devices have no hover: keep the open affordance visible.
        @media (hover: none) {
            &--doc &__open {
                opacity: 1;
            }
        }

        // --- Image card ---
        &--image {
            display: inline-flex;
            flex-direction: column;
            margin: 0;
        }

        &__thumb-wrap {
            position: relative;
            display: flex;
            // Fixed box: the card claims its final footprint before the
            // image decodes, so the message log never shifts on load.
            width: 280px;
            max-width: 100%;
            height: 200px;
            background-color: var(--color-surface);
            border-radius: var(--file-radius);
            overflow: hidden;
        }

        &__thumb-link {
            display: flex;
            width: 100%;
            height: 100%;
            cursor: pointer;
        }

        &__thumb {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &__caption {
            display: flex;
            flex-direction: column;
            gap: 1px;
            padding: var(--spacing-8);
            min-width: 0;
        }

        &__name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: var(--font-medium, 500);
            color: var(--color-word-1);
        }

        &__meta {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: var(--text-10);
            line-height: var(--leading-normal);
            // Reserve the line even while empty: image dimensions arrive
            // only after decode and must not grow the caption.
            min-height: 1.4em;
            color: var(--color-word-4);
            // Stable column for the dimensions/size figures.
            font-variant-numeric: tabular-nums;
        }

        &__error {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-4);
            color: var(--color-danger);

            .vv-icon {
                flex-shrink: 0;
            }
        }

        &__status {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            .vv-icon {
                width: var(--spacing-32);
                height: var(--spacing-32);
            }

            &--overlay {
                position: absolute;
                inset: 0;
                background-color: color-mix(
                    in srgb,
                    var(--color-surface-1) 70%,
                    transparent
                );
                color: var(--color-word-1);
            }

            &--error {
                color: var(--color-word-4);
            }
        }

        // --- Remove button (input context) ---
        &__remove {
            position: absolute;
            top: 0;
            right: 0;
            transform: translate(50%, -50%);
            display: flex;
            align-items: center;
            justify-content: center;
            // Visible glyph is 18px but the hit area stays a comfortable 28px.
            width: var(--spacing-28);
            height: var(--spacing-28);
            padding: 0;
            border: none;
            border-radius: var(--rounded-full, 9999px);
            background-color: color-mix(
                in srgb,
                var(--color-surface-1) 85%,
                transparent
            );
            color: var(--color-word-2);
            cursor: pointer;
            line-height: 1;
            box-shadow: var(--shadow-sm);
            transition:
                background-color 0.18s var(--ease-out),
                color 0.18s var(--ease-out),
                scale 0.12s var(--ease-out);

            &:hover {
                background-color: var(--color-surface-2);
                color: var(--color-word-1);
            }

            &:active {
                scale: 0.96;
            }

            // Inside the document row the button rides inline, not overlaid
            // (size is inherited from the base).
            &--inline {
                position: static;
                flex-shrink: 0;
                box-shadow: none;
                background-color: transparent;
                color: var(--color-word-4);

                &:hover {
                    background-color: var(--color-surface-3);
                    color: var(--color-word-1);
                }
            }
        }

        // --- Compact (composer) chips ---
        // ChatGPT/Gemini-style: every attachment is the SAME height and carries
        // an always-visible round remove control in the top-right corner — no
        // hover-revealed actions, no size mismatch between image and file chips.
        &--compact {
            // Shared chip height so image and file chips align in the row.
            --chip-size: var(--spacing-56);
            max-width: 240px;

            &.pk-chatbot-file-preview--image {
                width: var(--chip-size);
                height: var(--chip-size);
                border-radius: var(--rounded-md, 6px);
            }

            // Fill the square; the root's `overflow: hidden` rounds the corners.
            .pk-chatbot-file-preview__thumb-wrap,
            .pk-chatbot-file-preview__thumb-link {
                width: 100%;
                height: 100%;
            }

            &.pk-chatbot-file-preview--doc {
                height: var(--chip-size);
                gap: var(--spacing-8);
                padding: var(--spacing-8);
                // Reserve room so the corner remove button never overlaps the
                // name/meta text.
                padding-inline-end: var(--spacing-28);
            }

            // Square icon tile that fills the chip height, so file chips read at
            // the same scale as image thumbnails.
            .pk-chatbot-file-preview__icon-tile {
                align-self: stretch;
                width: auto;
                height: auto;
                aspect-ratio: 1;
            }

            .pk-chatbot-file-preview__icon {
                width: var(--spacing-20);
                height: var(--spacing-20);
            }
        }

        // Always-visible round remove control in the top-right corner. Sits
        // just inside the chip (an ancestor clips overflow), bigger than before,
        // with an extended hit area for easy tapping.
        &--compact &__remove {
            top: var(--spacing-4);
            right: var(--spacing-4);
            width: var(--spacing-24);
            height: var(--spacing-24);
            border: 1px solid var(--color-surface-4);
            background-color: var(--color-surface);
            color: var(--color-word-1);
            font-size: var(--text-16);
            box-shadow: var(--shadow-sm);

            // Comfortable touch/click target beyond the small visible glyph.
            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--spacing-4));
            }

            &:hover {
                background-color: var(--color-surface-2);
                color: var(--color-word-1);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            &--doc,
            &__open,
            &__remove {
                transition: none;
            }
        }
    }
</style>
